import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Loader2, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useDeleteCopiedProjectData, DeleteCopiedDataResult } from '@/hooks/projects/useDeleteCopiedProjectData';
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ProjectDeleteCopiedDataDialogProps {
  projectId: string;
  projectName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface CopiedDataStats {
  itemsCount: number;
  specsCount: number;
  drawingsCount: number;
}

export function ProjectDeleteCopiedDataDialog({ projectId, projectName, isOpen, onClose }: ProjectDeleteCopiedDataDialogProps) {
  const [deleteItems, setDeleteItems] = useState(true);
  const [deleteSpecs, setDeleteSpecs] = useState(true);
  const [deleteDrawings, setDeleteDrawings] = useState(true);
  const [stats, setStats] = useState<CopiedDataStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteResult, setDeleteResult] = useState<DeleteCopiedDataResult | null>(null);

  const deleteMutation = useDeleteCopiedProjectData();

  // Load stats of copied data
  useEffect(() => {
    if (!isOpen) return;

    const loadStats = async () => {
      setLoadingStats(true);
      try {
        const [itemsRes, specsRes, drawingsRes] = await Promise.all([
          supabase
            .from('items')
            .select('id', { count: 'exact', head: true })
            .eq('project_id', projectId)
            .like('notes', '%[Copied:%'),
          supabase
            .from('diary_spec_items')
            .select('id', { count: 'exact', head: true })
            .eq('project_id', projectId)
            .like('description', '%[Copied:%'),
          supabase
            .from('drawings')
            .select('id', { count: 'exact', head: true })
            .eq('project_id', projectId)
            .like('description', '%[Copied:%'),
        ]);

        setStats({
          itemsCount: itemsRes.count || 0,
          specsCount: specsRes.count || 0,
          drawingsCount: drawingsRes.count || 0,
        });
      } catch (error) {
        console.error('Failed to load copied data stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    loadStats();
  }, [projectId, isOpen]);

  const handleDelete = async () => {
    setShowConfirm(false);
    
    try {
      const result = await deleteMutation.mutateAsync({
        projectId,
        batchId: null, // Delete all copied data
        deleteItems,
        deleteSpecs,
        deleteDrawings,
      });
      
      setDeleteResult(result);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleClose = () => {
    setDeleteItems(true);
    setDeleteSpecs(true);
    setDeleteDrawings(true);
    setStats(null);
    setDeleteResult(null);
    onClose();
  };

  const hasSelection = deleteItems || deleteSpecs || deleteDrawings;
  const hasCopiedData = stats && (stats.itemsCount > 0 || stats.specsCount > 0 || stats.drawingsCount > 0);
  const totalToDelete = 
    (deleteItems ? stats?.itemsCount || 0 : 0) +
    (deleteSpecs ? stats?.specsCount || 0 : 0) +
    (deleteDrawings ? stats?.drawingsCount || 0 : 0);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Xóa dữ liệu đã copy
            </DialogTitle>
            <DialogDescription>
              Xóa dữ liệu đã copy vào: <strong>{projectName}</strong>
            </DialogDescription>
          </DialogHeader>

          {deleteResult ? (
            <div className="space-y-4">
              <Alert>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription>
                  <div className="font-medium text-green-600 mb-2">Xóa thành công!</div>
                  <ul className="text-sm space-y-1">
                    {deleteResult.items_deleted !== undefined && deleteResult.items_deleted > 0 && (
                      <li>• Items: {deleteResult.items_deleted} đã xóa</li>
                    )}
                    {deleteResult.specs_deleted !== undefined && deleteResult.specs_deleted > 0 && (
                      <li>• Specs: {deleteResult.specs_deleted} đã xóa</li>
                    )}
                    {deleteResult.drawings_deleted !== undefined && deleteResult.drawings_deleted > 0 && (
                      <li>• Drawings: {deleteResult.drawings_deleted} đã xóa</li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
              <DialogFooter>
                <Button onClick={handleClose}>Đóng</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Stats */}
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                {loadingStats ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Đang tìm dữ liệu đã copy...
                  </div>
                ) : stats ? (
                  hasCopiedData ? (
                    <div className="space-y-1">
                      <div className="font-medium">Dữ liệu đã copy trong dự án:</div>
                      <div className="text-muted-foreground">
                        {stats.itemsCount} items • {stats.specsCount} specs • {stats.drawingsCount} drawings
                      </div>
                    </div>
                  ) : (
                    <div className="text-muted-foreground">
                      Không tìm thấy dữ liệu đã copy trong dự án này.
                    </div>
                  )
                ) : null}
              </div>

              {hasCopiedData && (
                <>
                  {/* Module Selection */}
                  <div className="space-y-3">
                    <Label>Chọn dữ liệu cần xóa</Label>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="deleteItems" 
                        checked={deleteItems} 
                        onCheckedChange={(checked) => setDeleteItems(checked === true)}
                        disabled={stats?.itemsCount === 0}
                      />
                      <Label 
                        htmlFor="deleteItems" 
                        className={`font-normal cursor-pointer ${stats?.itemsCount === 0 ? 'text-muted-foreground' : ''}`}
                      >
                        Items ({stats?.itemsCount || 0})
                      </Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="deleteSpecs" 
                        checked={deleteSpecs} 
                        onCheckedChange={(checked) => setDeleteSpecs(checked === true)}
                        disabled={stats?.specsCount === 0}
                      />
                      <Label 
                        htmlFor="deleteSpecs" 
                        className={`font-normal cursor-pointer ${stats?.specsCount === 0 ? 'text-muted-foreground' : ''}`}
                      >
                        Specs ({stats?.specsCount || 0})
                      </Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="deleteDrawings" 
                        checked={deleteDrawings} 
                        onCheckedChange={(checked) => setDeleteDrawings(checked === true)}
                        disabled={stats?.drawingsCount === 0}
                      />
                      <Label 
                        htmlFor="deleteDrawings" 
                        className={`font-normal cursor-pointer ${stats?.drawingsCount === 0 ? 'text-muted-foreground' : ''}`}
                      >
                        Drawings ({stats?.drawingsCount || 0})
                      </Label>
                    </div>
                  </div>

                  {/* Warning */}
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      Hành động này không thể hoàn tác! Chỉ xóa dữ liệu có đánh dấu [Copied:...].
                    </AlertDescription>
                  </Alert>
                </>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={handleClose}>
                  {hasCopiedData ? 'Hủy' : 'Đóng'}
                </Button>
                {hasCopiedData && (
                  <Button 
                    variant="destructive"
                    onClick={() => setShowConfirm(true)} 
                    disabled={!hasSelection || deleteMutation.isPending}
                  >
                    {deleteMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Đang xóa...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Xóa ({totalToDelete})
                      </>
                    )}
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xác nhận xóa dữ liệu</AlertDialogTitle>
            <AlertDialogDescription>
              Bạn sắp xóa <strong>{totalToDelete}</strong> mục dữ liệu đã copy. Hành động này không thể hoàn tác.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Xóa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
